/**
 * Plaid Category to Tax Category Mapping System
 * Maps Plaid's transaction categories to our General, Federal, and State tax categories
 */

export interface CategoryMapping {
  general_category: string;
  federal_category?: string | null;
  oregon_category?: string | null;
  confidence: number;
  requires_context?: boolean;
  context_hint?: string;
}

/**
 * Comprehensive mapping from Plaid categories to our category system
 * Plaid uses a hierarchical system: Primary > Detailed > Confidence
 */
export const PLAID_TO_CATEGORY_MAPPING: Record<string, CategoryMapping> = {
  // ========================================
  // TRANSFER CATEGORIES
  // ========================================
  "Transfer.Debit": {
    general_category: "Transfer",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Transfer.Credit": {
    general_category: "Income",
    federal_category: null,
    oregon_category: null,
    confidence: 0.8,
  },
  "Transfer.Deposit": {
    general_category: "Income",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  "Transfer.Withdrawal": {
    general_category: "Transfer",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Transfer.Third Party": {
    general_category: "Transfer",
    federal_category: null,
    oregon_category: null,
    confidence: 0.7,
    requires_context: true,
    context_hint: "Could be payment for services or personal transfer",
  },

  // ========================================
  // FOOD AND DRINK
  // ========================================
  "Food and Drink.Restaurants": {
    general_category: "Restaurants",
    federal_category: "meals",
    oregon_category: null,
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business meal or personal?",
  },
  "Food and Drink.Restaurants.Fast Food": {
    general_category: "Fast Food",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Food and Drink.Restaurants.Coffee Shop": {
    general_category: "Coffee Shops",
    federal_category: "meals",
    oregon_category: null,
    confidence: 0.5,
    requires_context: true,
    context_hint: "Business meeting or personal?",
  },
  "Food and Drink.Groceries": {
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Food and Drink.Groceries.Supermarkets and Grocery Stores": {
    general_category: "Groceries",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Food and Drink.Bar": {
    general_category: "Bars and Nightlife",
    federal_category: "meals",
    oregon_category: null,
    confidence: 0.4,
    requires_context: true,
    context_hint: "Client entertainment or personal?",
  },

  // ========================================
  // TRANSPORTATION
  // ========================================
  "Transportation.Gas": {
    general_category: "Gas Stations",
    federal_category: "car_truck",
    oregon_category: "car_truck_expenses",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal vehicle?",
  },
  "Transportation.Parking": {
    general_category: "Parking",
    federal_category: "car_truck",
    oregon_category: "car_truck_expenses",
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business or personal parking?",
  },
  "Transportation.Public Transportation": {
    general_category: "Public Transportation",
    federal_category: "travel",
    oregon_category: "travel",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },
  "Transportation.Taxi": {
    general_category: "Taxi",
    federal_category: "travel",
    oregon_category: "travel",
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },
  "Transportation.Rideshare": {
    general_category: "Rideshare",
    federal_category: "travel",
    oregon_category: "travel",
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },
  "Transportation.Tolls": {
    general_category: "Transportation",
    federal_category: "car_truck",
    oregon_category: "car_truck_expenses",
    confidence: 0.7,
    requires_context: true,
  },
  "Transportation.Car Rental": {
    general_category: "Travel",
    federal_category: "travel",
    oregon_category: "travel",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },

  // ========================================
  // TRAVEL
  // ========================================
  "Travel.Airlines and Aviation Services": {
    general_category: "Airlines",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },
  "Travel.Lodging": {
    general_category: "Hotels",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal travel?",
  },
  "Travel.Cruises": {
    general_category: "Travel",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Travel.Car and Truck Rentals": {
    general_category: "Travel",
    federal_category: "travel_expenses",
    oregon_category: "travel",
    confidence: 0.7,
    requires_context: true,
  },

  // ========================================
  // SHOPS
  // ========================================
  "Shops.Clothing and Accessories": {
    general_category: "Clothing",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  "Shops.Electronics": {
    general_category: "Electronics",
    federal_category: "office_expense",
    oregon_category: "equipment",
    confidence: 0.5,
    requires_context: true,
    context_hint: "Business equipment or personal?",
  },
  "Shops.Computers and Electronics": {
    general_category: "Electronics",
    federal_category: "office_expense",
    oregon_category: "equipment",
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business equipment or personal?",
  },
  "Shops.Furniture and Home Decor": {
    general_category: "Furniture",
    federal_category: "utilities",
    oregon_category: null,
    confidence: 0.4,
    requires_context: true,
    context_hint: "Home office furniture?",
  },
  "Shops.Books and Magazines": {
    general_category: "Books",
    federal_category: "other_expenses",
    oregon_category: null,
    confidence: 0.5,
    requires_context: true,
    context_hint: "Professional development or personal?",
  },
  "Shops.Pharmacies": {
    general_category: "Pharmacy",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.9,
  },
  "Shops.Department Stores": {
    general_category: "Department Stores",
    federal_category: null,
    oregon_category: null,
    confidence: 0.7,
  },
  "Shops.Office Supplies": {
    general_category: "Office Supplies",
    federal_category: "office_expense",
    oregon_category: "supplies",
    confidence: 0.9,
  },
  "Shops.Sporting Goods": {
    general_category: "Sports and Outdoors",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  "Shops.Pet Supplies": {
    general_category: "Pet Supplies",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },

  // ========================================
  // SERVICE
  // ========================================
  "Service.Utilities": {
    general_category: "Utilities",
    federal_category: "utilities",
    oregon_category: "utilities",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Home office percentage?",
  },
  "Service.Electric": {
    general_category: "Electric",
    federal_category: "utilities",
    oregon_category: "utilities_electric",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Home office percentage?",
  },
  "Service.Gas Utility": {
    general_category: "Gas",
    federal_category: "utilities",
    oregon_category: "utilities_gas",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Home office percentage?",
  },
  "Service.Water": {
    general_category: "Water",
    federal_category: "utilities",
    oregon_category: "utilities_water",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Home office percentage?",
  },
  "Service.Telecommunication Services": {
    general_category: "Internet Service",
    federal_category: "utilities",
    oregon_category: "utilities_telecom",
    confidence: 0.85,
    requires_context: true,
    context_hint: "Business use percentage?",
  },
  "Service.Internet and Cable": {
    general_category: "Internet Service",
    federal_category: "utilities",
    oregon_category: "utilities_telecom",
    confidence: 0.85,
    requires_context: true,
    context_hint: "Business use percentage?",
  },
  "Service.Cellular Phone": {
    general_category: "Mobile Phone",
    federal_category: "utilities",
    oregon_category: "utilities_telecom",
    confidence: 0.8,
    requires_context: true,
    context_hint: "Business use percentage?",
  },
  "Service.Insurance": {
    general_category: "Insurance",
    federal_category: "insurance",
    oregon_category: "insurance",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal insurance?",
  },
  "Service.Legal": {
    general_category: "Legal",
    federal_category: "legal_professional",
    oregon_category: "professional_fees",
    confidence: 0.9,
  },
  "Service.Accounting": {
    general_category: "Accounting",
    federal_category: "legal_professional",
    oregon_category: "professional_fees",
    confidence: 0.95,
  },
  "Service.Consulting": {
    general_category: "Consulting",
    federal_category: "legal_professional",
    oregon_category: "professional_fees",
    confidence: 0.9,
  },
  "Service.Advertising and Marketing": {
    general_category: "Business Services",
    federal_category: "advertising",
    oregon_category: "marketing",
    confidence: 0.9,
  },
  "Service.Home Improvement": {
    general_category: "Home Improvement",
    federal_category: "utilities",
    oregon_category: null,
    confidence: 0.5,
    requires_context: true,
    context_hint: "Home office improvement?",
  },
  "Service.Home Maintenance": {
    general_category: "Home Maintenance",
    federal_category: "utilities",
    oregon_category: null,
    confidence: 0.5,
    requires_context: true,
    context_hint: "Home office maintenance?",
  },
  "Service.Cleaning": {
    general_category: "Cleaning",
    federal_category: "utilities",
    oregon_category: null,
    confidence: 0.4,
    requires_context: true,
    context_hint: "Office cleaning service?",
  },
  "Service.Subscription": {
    general_category: "Software",
    federal_category: "software_subscriptions",
    oregon_category: "business_software",
    confidence: 0.6,
    requires_context: true,
    context_hint: "Business software subscription?",
  },
  "Service.Financial": {
    general_category: "Professional Services",
    federal_category: "legal_professional",
    oregon_category: "professional_fees",
    confidence: 0.8,
  },

  // ========================================
  // HEALTHCARE
  // ========================================
  "Healthcare.Healthcare Services": {
    general_category: "Medical",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  "Healthcare.Medical Services": {
    general_category: "Medical",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  "Healthcare.Dental": {
    general_category: "Dental",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  "Healthcare.Vision": {
    general_category: "Vision",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  "Healthcare.Mental Health and Counseling": {
    general_category: "Mental Health",
    federal_category: "medical_expenses",
    oregon_category: "healthcare",
    confidence: 0.95,
  },
  "Healthcare.Veterinary Services": {
    general_category: "Pet Care",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },

  // ========================================
  // RECREATION
  // ========================================
  "Recreation.Sports": {
    general_category: "Sports and Outdoors",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  "Recreation.Gyms and Fitness Centers": {
    general_category: "Fitness",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Recreation.Movies and Entertainment": {
    general_category: "Entertainment",
    federal_category: "meals",
    oregon_category: null,
    confidence: 0.4,
    requires_context: true,
    context_hint: "Client entertainment or personal?",
  },
  "Recreation.Music and Audio": {
    general_category: "Music",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },
  "Recreation.Gaming": {
    general_category: "Gaming",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Recreation.Arts and Crafts": {
    general_category: "Hobbies",
    federal_category: null,
    oregon_category: null,
    confidence: 0.85,
  },

  // ========================================
  // COMMUNITY
  // ========================================
  "Community.Religious": {
    general_category: "Religious",
    federal_category: "other_expenses",
    oregon_category: null,
    confidence: 0.9,
  },
  "Community.Charitable Organizations": {
    general_category: "Charity",
    federal_category: "other_expenses",
    oregon_category: null,
    confidence: 0.95,
  },
  "Community.Education": {
    general_category: "Education",
    federal_category: "other_expenses",
    oregon_category: "education",
    confidence: 0.7,
    requires_context: true,
    context_hint: "Professional development or personal education?",
  },
  "Community.Government and Non-Profit": {
    general_category: "Government",
    federal_category: null,
    oregon_category: null,
    confidence: 0.8,
  },

  // ========================================
  // PERSONAL CARE
  // ========================================
  "Personal Care.Personal": {
    general_category: "Personal Care",
    federal_category: null,
    oregon_category: null,
    confidence: 0.9,
  },
  "Personal Care.Hair and Beauty": {
    general_category: "Beauty",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Personal Care.Spa": {
    general_category: "Spa",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Personal Care.Laundry and Dry Cleaning": {
    general_category: "Cleaning",
    federal_category: null,
    oregon_category: null,
    confidence: 0.8,
  },

  // ========================================
  // BANK AND FINANCIAL
  // ========================================
  "Bank Fees.ATM": {
    general_category: "ATM Fees",
    federal_category: "bank_fees",
    oregon_category: null,
    confidence: 0.95,
  },
  "Bank Fees.Foreign Transaction": {
    general_category: "Banking Fees",
    federal_category: "bank_fees",
    oregon_category: null,
    confidence: 0.9,
  },
  "Bank Fees.Overdraft": {
    general_category: "Banking Fees",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Bank Fees.Service Charge": {
    general_category: "Banking Fees",
    federal_category: "bank_fees",
    oregon_category: null,
    confidence: 0.8,
  },
  "Payment.Credit Card": {
    general_category: "Credit Cards",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Payment.Mortgage": {
    general_category: "Mortgage",
    federal_category: "mortgage_interest",
    oregon_category: "housing",
    confidence: 0.95,
  },
  "Payment.Rent": {
    general_category: "Rent",
    federal_category: "rent_lease",
    oregon_category: "housing",
    confidence: 0.95,
  },
  "Payment.Loan": {
    general_category: "Loans",
    federal_category: "interest",
    oregon_category: null,
    confidence: 0.7,
    requires_context: true,
    context_hint: "Business or personal loan?",
  },

  // ========================================
  // TAX
  // ========================================
  "Tax.Income Tax": {
    general_category: "Taxes",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Tax.Sales Tax": {
    general_category: "Taxes",
    federal_category: null,
    oregon_category: null,
    confidence: 0.95,
  },
  "Tax.Property Tax": {
    general_category: "Taxes",
    federal_category: "property_tax",
    oregon_category: "property_tax",
    confidence: 0.95,
  },
};

/**
 * Map Plaid category array to our category system
 */
export function mapPlaidCategory(
  plaidCategories: string[] | string | undefined
): CategoryMapping | null {
  if (!plaidCategories) return null;

  // Handle both array and string formats
  const categoryArray = Array.isArray(plaidCategories)
    ? plaidCategories
    : [plaidCategories];

  // Try to find the most specific category first
  const fullCategory = categoryArray.join(".");
  if (PLAID_TO_CATEGORY_MAPPING[fullCategory]) {
    return PLAID_TO_CATEGORY_MAPPING[fullCategory];
  }

  // Try primary category
  if (categoryArray.length > 0 && PLAID_TO_CATEGORY_MAPPING[categoryArray[0]]) {
    return PLAID_TO_CATEGORY_MAPPING[categoryArray[0]];
  }

  // Try primary.secondary
  if (categoryArray.length > 1) {
    const partial = `${categoryArray[0]}.${categoryArray[1]}`;
    if (PLAID_TO_CATEGORY_MAPPING[partial]) {
      return PLAID_TO_CATEGORY_MAPPING[partial];
    }
  }

  return null;
}

/**
 * Get all categories that require context
 */
export function getCategoriesRequiringContext(): string[] {
  return Object.entries(PLAID_TO_CATEGORY_MAPPING)
    .filter(([_, mapping]) => mapping.requires_context)
    .map(([category, _]) => category);
}

/**
 * Suggest category based on Plaid data
 */
export function suggestCategoryFromPlaid(
  plaidCategories: string[] | string | undefined,
  merchantName?: string
): (CategoryMapping & { source: "plaid" | "merchant" | "unknown" }) | null {
  const plaidMapping = mapPlaidCategory(plaidCategories);

  if (plaidMapping) {
    return {
      ...plaidMapping,
      source: "plaid",
    };
  }

  return null;
}
